import type { FC } from 'react'
import { useState } from 'react'

import { MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { ONE_TIME_ORDER_INTERVAL } from '@skytech/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import ChevronRightIcon from 'mdi-react/ChevronRightIcon'
import LockOutlineIcon from 'mdi-react/LockOutlineIcon'
import { useParams } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import CheckboxIcon from '@/components/CheckboxIcon'
import Highlight from '@/components/Highlight'
import RouterLink from '@/components/RouterLink'
import { useIsAdmin, useIsThreeStepIT } from '@/hooks/access'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import type { EcomPolicySubscription } from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'
import { numberFormat } from '@/util/formatters/numbers'

import './PolicySubscriptionItem.styles.scss'

type PopupType = 'remove' | 'add' | 'unlock-ordering'

const PolicySubscriptionItem: FC<
  Pick<
    EcomPolicySubscription,
    | 'policyDescription'
    | 'policyId'
    | 'vendorName'
    | 'isLocked'
    | 'policyName'
    | 'coverAmount'
    | 'orderInterval'
  > & {
    searchFilter: string
    isSubscribed: boolean
    leasingEndOfLeaseTypeId?: number | null
    hideCoverInterval?: boolean
  }
> = ({
  policyName,
  policyId,
  policyDescription,
  vendorName,
  coverAmount,
  orderInterval,
  searchFilter,
  isLocked,
  isSubscribed,
  leasingEndOfLeaseTypeId,
  hideCoverInterval,
}) => {
  const isAdmin = useIsAdmin()

  const isThreeStepIT = useIsThreeStepIT()
  const [isSubscribedToPolicy, setIsSubscribedToPolicy] = useState(isSubscribed)
  const translate = useTranslation()
  const { formatNumber } = useFormattedNumberWithCurrency()
  const queryClient = useQueryClient()
  const { costObjectId } = useParams()
  const [openPopup, setOpenPopup] = useState<PopupType | null>(null)

  const deletePolicySubMutation = useMutation(adminResources.deleteCostObjectPolicySubscription, {
    onMutate() {
      setIsSubscribedToPolicy(false)
    },
    onSuccess() {
      queryClient.invalidateQueries(queries.adminCostObjectDetails(Number(costObjectId)))
      setOpenPopup(null)
    },
  })

  const unlockOrderingOnPolicySubMutation = useMutation(
    adminResources.unlockOrderingOnCostObjectPolicySubscription,
    {
      onSuccess() {
        queryClient.invalidateQueries(queries.adminCostObjectDetails(Number(costObjectId)))
        setOpenPopup(null)
      },
    }
  )

  const addPolicySubMutation = useMutation(adminResources.postCostObjectPolicySubscription, {
    onMutate() {
      setIsSubscribedToPolicy(true)
    },
    onSuccess() {
      queryClient.invalidateQueries(queries.adminCostObjectDetails(Number(costObjectId)))
      setOpenPopup(null)
    },
  })

  function handleClickSub() {
    if (!isAdmin) return

    if (isSubscribed) {
      setOpenPopup('remove')
      return
    }

    setOpenPopup('add')
  }

  const isRowLoading = deletePolicySubMutation.isLoading || addPolicySubMutation.isLoading
  const {
    text: popupText,
    title: popupTitle,
    submitButtonText,
    type: popupType,
  } = getPopupTexts(openPopup)

  function handleClickPopupConfirmButton() {
    switch (openPopup) {
      case 'add':
        return addPolicySubMutation.mutate({ costObjectId: Number(costObjectId), policyId })
      case 'remove':
        return deletePolicySubMutation.mutate({ costObjectId: Number(costObjectId), policyId })
      case 'unlock-ordering':
        return unlockOrderingOnPolicySubMutation.mutate({
          costObjectId: Number(costObjectId),
          policyId,
        })
      default:
        throw Error('Popup type not supported')
    }
  }

  return (
    <>
      <tr
        className={twJoin(
          'policy-subscription',
          isSubscribedToPolicy && 'is-subscribed',
          isRowLoading && 'is-loading',
          !isAdmin && '!cursor-default'
        )}
        onClick={handleClickSub}
      >
        <CheckboxIcon isSelected={isSubscribedToPolicy} />
        <td>
          <div>
            <Highlight pattern={searchFilter} text={policyName} />
          </div>
          <div>
            <small>
              <Highlight pattern={searchFilter} text={vendorName} />
            </small>
          </div>
        </td>
        <td className="is-mobile-hidden">
          <Highlight pattern={searchFilter} text={policyDescription} />
        </td>
        <td className="is-mobile-hidden text-right">
          <Highlight pattern={searchFilter} text={formatNumber({ amount: coverAmount || 0 })} />
        </td>
        {!hideCoverInterval && (
          <td className="is-mobile-hidden text-right">
            {leasingEndOfLeaseTypeId === 2 ? (
              <span>{' - '}</span>
            ) : (
              <Highlight
                pattern={searchFilter}
                text={
                  orderInterval === ONE_TIME_ORDER_INTERVAL
                    ? `${translate('One-time order')}`
                    : `${numberFormat(orderInterval)} ${translate('mos')}`
                }
              />
            )}
          </td>
        )}
        <td className="is-mobile-hidden" />

        <td className="is-mobile-hidden unlock-ordering">
          {Boolean(isSubscribed && isLocked) && isAdmin && (
            <Button
              variant="tertiary"
              className="m-0 p-0 disabled:bg-transparent"
              disabled={!isLocked}
              onClick={e => {
                e.stopPropagation()
                setOpenPopup('unlock-ordering')
              }}
            >
              <div className="action">
                <h4>
                  <Translation id="Unlock ordering" />
                </h4>
                <LockOutlineIcon className={isLocked ? 'is-policy-locked' : ''} />
              </div>
            </Button>
          )}
        </td>

        {isAdmin && (
          <td>
            <RouterLink
              to={APP_ROUTES.HARDWARE_ECOM_POLICY.replace(':ecomPolicyId', String(policyId))}
              colorMode={isThreeStepIT ? undefined : 'black'}
              onClick={e => {
                e.stopPropagation()
              }}
            >
              <div className="action">
                <h4>
                  <Translation id="Go to policy" />
                </h4>
                <ChevronRightIcon />
              </div>
            </RouterLink>
          </td>
        )}
      </tr>

      <MessagePopup
        className="confirm-policy-sub"
        type={popupType}
        title={popupTitle}
        variant="narrow"
        message={
          <>
            <div className="mb-5">
              <Translation id="Policy" />: {policyName}
            </div>
            <Translation className="block" id={popupText} />
          </>
        }
        isOpen={Boolean(openPopup)}
        onClose={() => setOpenPopup(null)}
      >
        <footer>
          <Button variant="tertiary" onClick={() => setOpenPopup(null)}>
            <Translation id="Cancel" />
          </Button>
          <Button
            isLoading={addPolicySubMutation.isLoading || deletePolicySubMutation.isLoading}
            onClick={handleClickPopupConfirmButton}
          >
            <Translation id={submitButtonText} />
          </Button>
        </footer>
      </MessagePopup>
    </>
  )
}

export default PolicySubscriptionItem

function getPopupTexts(popupType: PopupType | null) {
  if (popupType === null)
    return { title: '', text: '', submitButtonText: '', type: 'warning' as const }

  switch (popupType) {
    case 'add':
      return {
        title: 'Add policy confirmation',
        text: 'Are you sure you want to add this policy subscription?',
        submitButtonText: 'Add',
        type: 'success' as const,
      }
    case 'remove':
      return {
        title: 'Remove policy confirmation',
        text: 'Are you sure you want to remove this policy subscription?',
        submitButtonText: 'Remove',
        type: 'error' as const,
      }
    case 'unlock-ordering':
      return {
        title: 'Unlock ordering confirmation',
        text: 'Are you sure you want to unlock ordering for this policy?',
        submitButtonText: 'Unlock',
        type: 'warning' as const,
      }
    default:
      throw Error('Popup type not supported')
  }
}
